import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import { formatCurrency, formatDateTime, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  FileText,
  Loader2
} from 'lucide-react';

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const response = await api.getOrders();
      setOrders(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.exportOrdersCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Orders-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export erfolgreich');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.order_number.includes(searchTerm) ||
      order.customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bestellungen
          </h1>
          <p className="text-slate-600 mt-1">
            {filteredOrders.length} von {orders.length} Bestellungen
          </p>
        </div>
        {user?.role === 'admin' && (
          <Button 
            onClick={handleExportCSV}
            disabled={exporting}
            variant="outline"
            className="gap-2"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            CSV Export (Bestätigt)
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen nach Nr., Name oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-orders"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="sent">Versendet</SelectItem>
                <SelectItem value="confirmed">Bestätigt</SelectItem>
                <SelectItem value="cancelled">Storniert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">Keine Bestellungen gefunden</p>
            <p className="text-slate-400 text-sm mt-1">
              {searchTerm || statusFilter !== 'all' 
                ? 'Versuchen Sie andere Suchkriterien'
                : 'Erstellen Sie Ihre erste Bestellung'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Bestellnr.</TableHead>
                <TableHead className="font-semibold">Kunde</TableHead>
                <TableHead className="font-semibold hidden md:table-cell">Produkt</TableHead>
                <TableHead className="font-semibold hidden lg:table-cell">Erstellt</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Monatlich</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow 
                  key={order.id} 
                  className="table-row-hover cursor-pointer"
                  data-testid={`order-row-${order.order_number}`}
                >
                  <TableCell className="font-medium">
                    <Link to={`/orders/${order.id}`} className="text-[#F47920] hover:underline">
                      #{order.order_number}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">
                        {order.customer.first_name} {order.customer.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{order.customer.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <p className="text-slate-600">{order.product_name}</p>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-500">
                    {formatDateTime(order.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLORS[order.status]}>
                      {STATUS_LABELS[order.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(order.monthly_total)}
                  </TableCell>
                  <TableCell>
                    <Link to={`/orders/${order.id}`}>
                      <Button variant="ghost" size="icon" data-testid={`view-order-${order.order_number}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default Orders;
